import React, { useState } from 'react';
import { 
  HardHat, 
  FileCheck, 
  ShieldCheck, 
  Grid, 
  Zap, 
  Lock, 
  Building2, 
  ArrowRight,
  X,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

export interface RoleCard {
  id: 'foreman' | 'pto' | 'hr' | 'admin';
  title: 'Прораб' | 'Инженер ПТО' | 'Специалист по ОТ и Кадрам' | 'Главный Инженер';
  subtitle: string;
  pin: string;
  path: string;
  icon: any;
  accentColor: string;
  bgGradient: string;
  badgeText: string;
  badgeStyle: string;
  features: string[];
}

interface LauncherModuleProps {
  onLoginSuccess: (role: 'foreman' | 'pto' | 'hr' | 'admin', path: string) => void;
}

export const LauncherModule: React.FC<LauncherModuleProps> = ({ onLoginSuccess }) => {
  const [selectedRole, setSelectedRole] = useState<RoleCard | null>(null);
  const [pinInput, setPinInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const roleCards: RoleCard[] = [
    {
      id: 'foreman',
      title: 'Прораб',
      subtitle: 'Полевой PWA-портал бригадира',
      pin: '0000',
      path: '/foreman',
      icon: HardHat,
      accentColor: 'text-amber-600',
      bgGradient: 'from-amber-500/10 via-white to-white',
      badgeText: 'PIN: 0000',
      badgeStyle: 'bg-amber-50 text-amber-700 border-amber-200',
      features: ['Утренний чек-ин звеньев', 'Вечерний рапорт объемов', 'Фиксация простоев & фото']
    },
    {
      id: 'pto',
      title: 'Инженер ПТО',
      subtitle: 'Портал ПТО, АОСР & Ведомостей',
      pin: '1111',
      path: '/pto',
      icon: FileCheck,
      accentColor: 'text-emerald-600',
      bgGradient: 'from-emerald-500/10 via-white to-white',
      badgeText: 'PIN: 1111',
      badgeStyle: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      features: ['Лента сдачи объемов', 'Генератор актов АОСР (РД)', 'Реестр паспортов & КС-6а']
    },
    {
      id: 'hr',
      title: 'Специалист по ОТ и Кадрам',
      subtitle: 'Кадровый портал & Охрана Труда',
      pin: '2222',
      path: '/hr',
      icon: ShieldCheck,
      accentColor: 'text-blue-600',
      bgGradient: 'from-blue-500/10 via-white to-white',
      badgeText: 'PIN: 2222',
      badgeStyle: 'bg-blue-50 text-blue-700 border-blue-200',
      features: ['Картотека электромонтажников', 'Группы ЭБ (II-V) & Ростехнадзор', 'Печать QR-пропусков / бейджей']
    },
    {
      id: 'admin',
      title: 'Главный Инженер',
      subtitle: 'Панель Управления & 2D-Шахматка',
      pin: '3333',
      path: '/admin',
      icon: Grid,
      accentColor: 'text-purple-600',
      bgGradient: 'from-purple-500/10 via-white to-white',
      badgeText: 'PIN: 3333',
      badgeStyle: 'bg-purple-50 text-purple-700 border-purple-200',
      features: ['2D-Матрица стояков (Шахматка)', 'Конструктор форм рапортов', 'Справочники работ & материалов']
    }
  ];

  const handleCardClick = (card: RoleCard) => {
    setSelectedRole(card);
    setPinInput('');
    setErrorMessage(null);
  };

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole) return;

    if (pinInput === selectedRole.pin) {
      localStorage.setItem('construction_monitor_role', selectedRole.id);
      onLoginSuccess(selectedRole.id, selectedRole.path);
    } else {
      setErrorMessage(`Неверный PIN-код! Для роли «${selectedRole.title}» введите PIN: ${selectedRole.pin}`);
      setPinInput('');
    }
  };

  const handleKeypadDigit = (digit: string) => {
    if (pinInput.length < 4) {
      const updated = pinInput + digit;
      setPinInput(updated);
      setErrorMessage(null);
      // Auto submit on 4 digits
      if (updated.length === 4 && selectedRole) {
        if (updated === selectedRole.pin) {
          localStorage.setItem('construction_monitor_role', selectedRole.id);
          onLoginSuccess(selectedRole.id, selectedRole.path);
        } else {
          setErrorMessage(`Неверный PIN-код! Подсказка: ${selectedRole.pin}`);
          setPinInput('');
        }
      }
    }
  };

  const handleKeypadBackspace = () => {
    setPinInput(prev => prev.slice(0, -1));
    setErrorMessage(null);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col font-sans">
      {/* Top Banner Header */}
      <header className="bg-white border-b border-slate-200/80 shadow-sm py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl text-white font-black shadow-md shadow-amber-500/20">
              <Zap className="w-6 h-6 fill-current" />
            </div>
            <div>
              <h1 className="text-xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2">
                <span>Монитор Электромонтажа</span>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold">
                  Light Fintech UI
                </span>
              </h1>
              <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                <span>ЖК «Северная Звезда» • Подрядчик: ООО «ЭлектроМонтажСтрой»</span>
              </p>
            </div>
          </div>

          <div className="hidden sm:flex items-center space-x-2 text-xs bg-slate-100 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-600">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>Изолированный Ролевой Launcher</span>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-8 sm:py-12 flex flex-col justify-center space-y-8">
        <div className="text-center max-w-2xl mx-auto space-y-2">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            Выберите ролевой портал доступа
          </h2>
          <p className="text-sm text-slate-600">
            Для перехода в рабочую область нажмите на соответствующую финтех-карточку и введите 4-значный ролевой PIN-код.
          </p>
        </div>

        {/* 4 Fintech Access Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {roleCards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.id}
                onClick={() => handleCardClick(card)}
                className={`bg-gradient-to-b ${card.bgGradient} border border-slate-200/90 rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-xl hover:border-slate-300 transition-all duration-200 cursor-pointer group flex flex-col justify-between relative overflow-hidden active:scale-98`}
              >
                {/* Card Top Header */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className={`p-3 rounded-2xl bg-white border border-slate-200 shadow-sm ${card.accentColor} group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full border font-bold ${card.badgeStyle}`}>
                      {card.badgeText}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-slate-900 group-hover:text-amber-600 transition-colors">
                      {card.title}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {card.subtitle}
                    </p>
                  </div>

                  {/* Bullet features */}
                  <ul className="space-y-1.5 pt-2 border-t border-slate-100 text-[11px] text-slate-600">
                    {card.features.map((feat, idx) => (
                      <li key={idx} className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Card Footer Button */}
                <div className="pt-6 flex items-center justify-between text-xs font-bold text-slate-800 group-hover:text-amber-600">
                  <span className="flex items-center gap-1">
                    <Lock className="w-3.5 h-3.5 text-slate-400" />
                    <span>Войти по PIN</span>
                  </span>
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-4 px-6 text-center text-xs text-slate-500 max-w-6xl mx-auto w-full">
        <div>
          Монитор строительства • Электромонтажные работы (ЭОМ/ЭС/СС) &copy; 2026
        </div>
      </footer>

      {/* PIN Entrance Modal Dialog */}
      {selectedRole && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl space-y-6 relative">
            <button
              onClick={() => setSelectedRole(null)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Header */}
            <div className="text-center space-y-2">
              <div className={`w-14 h-14 rounded-2xl bg-white border border-slate-200 shadow-md ${selectedRole.accentColor} mx-auto flex items-center justify-center`}>
                <selectedRole.icon className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-extrabold text-slate-900">
                Вход: {selectedRole.title}
              </h3>
              <p className="text-xs text-slate-500">
                Введите 4-значный ролевой PIN-код для подтверждения доступа ({selectedRole.badgeText})
              </p>
            </div>

            {/* PIN Display Indicators */}
            <form onSubmit={handlePinSubmit} className="space-y-4">
              <div className="flex justify-center items-center space-x-3">
                {[0, 1, 2, 3].map((idx) => (
                  <div
                    key={idx}
                    className={`w-12 h-14 rounded-2xl border-2 flex items-center justify-center text-xl font-bold transition-all ${
                      pinInput.length > idx
                        ? 'border-amber-500 bg-amber-50 text-slate-900 shadow-sm'
                        : 'border-slate-200 bg-slate-50 text-slate-300'
                    }`}
                  >
                    {pinInput.length > idx ? '●' : ''}
                  </div>
                ))}
              </div>

              {errorMessage && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs font-semibold flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Onscreen Keypad */}
              <div className="grid grid-cols-3 gap-2.5 pt-2">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                  <button
                    key={digit}
                    type="button"
                    onClick={() => handleKeypadDigit(digit)}
                    className="py-3 bg-slate-50 hover:bg-slate-100 active:bg-amber-100 border border-slate-200 rounded-2xl text-lg font-bold text-slate-800 transition active:scale-95 shadow-sm"
                  >
                    {digit}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPinInput('')}
                  className="py-3 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-2xl text-xs font-bold text-slate-600 transition"
                >
                  Сброс
                </button>
                <button
                  type="button"
                  onClick={() => handleKeypadDigit('0')}
                  className="py-3 bg-slate-50 hover:bg-slate-100 active:bg-amber-100 border border-slate-200 rounded-2xl text-lg font-bold text-slate-800 transition active:scale-95 shadow-sm"
                >
                  0
                </button>
                <button
                  type="button"
                  onClick={handleKeypadBackspace}
                  className="py-3 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-2xl text-xs font-bold text-slate-600 transition"
                >
                  ⌫
                </button>
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-2xl shadow-md shadow-amber-500/20 transition text-sm mt-2"
              >
                Подтвердить вход
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LauncherModule;
